// src/oura/ask/liveFacts.ts
//
// Ask Oura: the client's own AI check, scores, site issues, Google search and
// setup, read from the same calls the Overview screen makes (GET /businesses/{id},
// the score history and the connectors). Before this file, the chat read the
// three sample businesses' fixtures for every client, so a real client always
// heard "no AI check yet" while Overview showed 32 of 50 (Jack, 29 Oct 2026).
// A sample business keeps its fixture, so the preview still has numbers.
// Reads only. A failed read is `error`, never empty facts.

import { useEffect, useState } from "react";
import { getBusinessConnectors, getBusinessDetail, readScoreHistory, type AeoBusinessDetail, type AeoConnector, type AeoScoreHistoryEntry } from "@service/aeo";
import { getAudit } from "../audit";
import { getBusiness, getSnapshot } from "../data";
import { adaptBusinessAudit, adaptConnectors, adaptSearchPerformance, buildLiveSnapshot } from "../liveAudit";
import { deriveGscState, findGscConnector } from "../gscState";
import { visibilityFixture } from "../visibility/fixtures";
import { buildVisModel, visibilityInputsFromAssessment } from "../visibility/fromLive";
import type { VisibilityInputs } from "../visibility/fromLive";
import type { VisModel } from "../visibility/model";

export interface AskScores {
    aeo: number | null;
    siteHealth: number | null;
    /** The scan before, where the history has one with a score. */
    prev: { aeo: number | null; siteHealth: number | null; at: string } | null;
}

export interface AskSearch {
    connected: boolean;
    clicks: number | null;
    impressions: number | null;
    avgPosition: number | null;
    /** Clicks in the window before this one, or null. */
    prevClicks: number | null;
    days: number | null;
    topPages: { url: string; clicks: number }[];
    /** The Search Console state in the Overview tile's own words ("Connected, page clicks only"), when the connectors were read. */
    state: { label: string; detail: string; hasData: boolean } | null;
}

export interface AskIssue {
    key: string;
    label: string;
    severity: "error" | "warning" | "notice";
    pages: number;
}

export interface AskFacts {
    name: string | null;
    domain: string | null;
    category: string | null;
    city: string | null;
    /** The latest scan's date, or null when never scanned. */
    lastScanAt: string | null;
    /** The next automatic check, or null when monthly checks are off. */
    nextScanAt: string | null;
    scores: AskScores | null;
    search: AskSearch | null;
    /** Every issue the Site screen lists with a count, worst first. */
    issues: AskIssue[] | null;
    connectors: { label: string; status: AeoConnector["status"] }[] | null;
    /** The Visibility model for this scan, for the question lists and the per-engine answers. */
    vis: VisModel | null;
    visInputs: VisibilityInputs | null;
    /** The business is one of the bundled samples: its facts come from fixtures. */
    fixture: boolean;
}

export interface LiveFactsRead {
    facts: AskFacts | null;
    /** The reads are still running (null facts, no error). */
    loading: boolean;
    /** The business detail or the score history could not be read. */
    error: boolean;
    retry: () => void;
}

const order = { error: 0, warning: 1, notice: 2 } as const;

function issuesOf(list: { key: string; label: string; severity: "error" | "warning" | "notice"; count: number }[]): AskIssue[] {
    return list
        .filter((i) => i.count > 0)
        .sort((a, b) => order[a.severity] - order[b.severity] || b.count - a.count)
        .map((i) => ({ key: i.key, label: i.label, severity: i.severity, pages: i.count }));
}

/** The sample businesses: the same fixtures the screens show, so the chat and the screen agree. */
function fixtureFacts(businessId: string): AskFacts | null {
    const business = getBusiness(businessId);
    const snapshot = getSnapshot(businessId);
    if (!business || !snapshot) return null;
    const inputs = visibilityFixture(businessId);
    const vis = inputs ? buildVisModel(inputs, null) : null;
    const audit = getAudit(business, snapshot);
    return {
        name: business.name,
        domain: business.domain,
        category: business.vertical ?? null,
        city: business.city ?? null,
        lastScanAt: snapshot.capturedAt,
        nextScanAt: null,
        scores: { aeo: audit.aeoScore, siteHealth: audit.siteHealthScore, prev: null },
        search: null,
        issues: issuesOf(audit.issues),
        connectors: null,
        vis,
        visInputs: inputs,
        fixture: true,
    };
}

function scoresOf(detail: AeoBusinessDetail, history: AeoScoreHistoryEntry[]): AskScores | null {
    const audit = detail.latest_assessment?.results?.businessAudit;
    if (!audit) return null;
    // The scan before the latest one with a score: a failed or gated run has none and is skipped.
    const scored = history.filter((h) => h.aeo_score !== null || h.seo_score !== null);
    const prev = scored.find((h) => h.assessment_id !== detail.latest_assessment?.assessment_id) ?? null;
    return {
        aeo: typeof audit.aeoScore === "number" ? audit.aeoScore : null,
        siteHealth: typeof audit.siteHealthScore === "number" ? audit.siteHealthScore : null,
        prev: prev ? { aeo: prev.aeo_score, siteHealth: prev.seo_score, at: prev.captured_at } : null,
    };
}

function searchOf(detail: AeoBusinessDetail, connectors: AeoConnector[] | null, audit: ReturnType<typeof adaptBusinessAudit>): AskSearch | null {
    const sp = adaptSearchPerformance(detail);
    // The same state the Overview tile derives, so the chat never calls a connection unread when the tile says otherwise.
    const gsc = connectors?.length
        ? deriveGscState({
              connector: findGscConnector(adaptConnectors(connectors)),
              search: sp,
              crawledPages: audit?.pagesCrawled ?? null,
              siteDomain: detail.domain,
              pageClicks: audit?.priorityPages.some((p) => p.visits > 0) ?? false,
          })
        : null;
    if (!sp && !gsc) return null;
    return {
        connected: sp?.connected ?? gsc?.kind !== "not_connected",
        clicks: sp?.totals?.clicks ?? null,
        impressions: sp?.totals?.impressions ?? null,
        avgPosition: sp?.totals?.avgPosition ?? null,
        prevClicks: null,
        days: sp?.window?.days ?? null,
        topPages: [...(sp?.pages ?? [])].sort((a, b) => b.clicks - a.clicks).slice(0, 3).map((p) => ({ url: p.url, clicks: p.clicks })),
        state: gsc ? { label: gsc.label, detail: gsc.detail, hasData: gsc.hasData } : null,
    };
}

function liveFacts(detail: AeoBusinessDetail, history: AeoScoreHistoryEntry[], connectors: AeoConnector[] | null): AskFacts {
    const snapshot = buildLiveSnapshot(detail);
    const la = detail.latest_assessment;
    const inputs = la ? visibilityInputsFromAssessment(la, { capturedAt: snapshot.capturedAt, history }) : null;
    const vis = inputs && inputs.vis ? buildVisModel(inputs, null) : null;
    const audit = adaptBusinessAudit(detail);
    return {
        name: detail.business_name,
        domain: detail.domain,
        category: detail.category ?? null,
        city: detail.city ?? null,
        lastScanAt: snapshot.capturedAt || null,
        nextScanAt: detail.next_auto_scan_at ?? null,
        scores: scoresOf(detail, history),
        search: searchOf(detail, connectors, audit),
        issues: audit ? issuesOf(audit.issues) : null,
        connectors: connectors ? connectors.map((c) => ({ label: c.label, status: c.status })) : null,
        vis,
        visInputs: inputs,
        fixture: false,
    };
}

/**
 * The client's facts, as the screens have them. A sample business answers at
 * once from its fixture; a live business reads the detail, the score history
 * and the connectors, the three reads the workspace already makes for it.
 */
export function useLiveFacts(businessId: string): LiveFactsRead {
    const fixture = !!getBusiness(businessId);
    const [attempt, setAttempt] = useState(0);
    const [read, setRead] = useState<{ key: string; facts: AskFacts | null; error: boolean } | null>(null);
    useEffect(() => {
        if (fixture) return;
        let live = true;
        const key = `${businessId}|${attempt}`;
        // The history is required: an empty list means "one scan", so a failed read
        // (null) is the error state, never a false "only one scan". Connectors stay
        // optional because their null is rendered as "not read yet".
        void Promise.all([getBusinessDetail(businessId), readScoreHistory(businessId), getBusinessConnectors(businessId).catch(() => null)]).then(
            ([detail, history, connectors]) => {
                if (!live) return;
                if (!detail.ok || history === null) {
                    setRead({ key, facts: null, error: true });
                    return;
                }
                setRead({ key, facts: liveFacts(detail.detail, history, connectors), error: false });
            },
            () => {
                if (live) setRead({ key, facts: null, error: true });
            },
        );
        return () => {
            live = false;
        };
    }, [businessId, attempt, fixture]);
    if (fixture) return { facts: fixtureFacts(businessId), loading: false, error: false, retry: () => undefined };
    const mine = read && read.key === `${businessId}|${attempt}` ? read : null;
    return { facts: mine?.facts ?? null, loading: !mine, error: !!mine?.error, retry: () => setAttempt((a) => a + 1) };
}
